import { z } from 'zod';
import { CYCLE_TYPES } from '../enums';
import { emailSchema, idSchema, moneySchema, phoneSchema } from './common';
// =============================================================================
// Operations de maintenance (ServiceType)
// -----------------------------------------------------------------------------
// Niveau superieur au forfait, exige par l'enonce : « Le client selectionne une
// operation de maintenance. Pour une maintenance, le client choisit un forfait. »
// =============================================================================
export const createServiceTypeSchema = z.object({
    name: z.string().trim().min(2, 'Nom requis.').max(100),
    slug: z
        .string()
        .trim()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug invalide (minuscules et tirets).')
        .max(100),
    description: z.string().trim().max(1000).optional().nullable(),
    /** Nom d'icone lucide-react, affiche dans le tunnel de reservation. */
    icon: z.string().trim().max(60).optional().nullable(),
    displayOrder: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
});
export const updateServiceTypeSchema = createServiceTypeSchema.partial();
export const serviceTypeSchema = createServiceTypeSchema.extend({
    id: idSchema,
    packageCount: z.number().int().optional(),
});
// =============================================================================
// Forfaits
// =============================================================================
export const createPackageSchema = z.object({
    serviceTypeId: idSchema,
    name: z.string().trim().min(2, 'Nom requis.').max(100),
    description: z.string().trim().max(2000).optional().nullable(),
    /**
     * La duree du forfait determine le nombre de creneaux consecutifs consommes (RG-03).
     * Bornee a 8 h : au-dela, l'intervention releve de l'atelier, pas du domicile.
     */
    durationMin: z
        .number()
        .int()
        .min(15, 'Duree minimale : 15 minutes.')
        .max(480, 'Duree maximale : 8 heures.'),
    price: moneySchema,
    /** `null` = forfait universel, compatible velo musculaire et VAE (RG-06). */
    cycleType: z.enum(CYCLE_TYPES).nullable().default(null),
    displayOrder: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
});
export const updatePackageSchema = createPackageSchema.partial();
export const packageSchema = createPackageSchema.extend({
    id: idSchema,
    serviceType: serviceTypeSchema.pick({ id: true, name: true, slug: true }).optional(),
});
// =============================================================================
// Produits additionnels
// =============================================================================
export const createProductSchema = z.object({
    name: z.string().trim().min(2, 'Nom requis.').max(150),
    description: z.string().trim().max(2000).optional().nullable(),
    price: moneySchema,
    category: z.string().trim().min(2).max(80),
    reference: z.string().trim().max(60).optional().nullable(),
    imageUrl: z.string().url('URL invalide.').max(512).optional().nullable(),
    isActive: z.boolean().default(true),
});
export const updateProductSchema = createProductSchema.partial();
export const productSchema = createProductSchema.extend({ id: idSchema });
// =============================================================================
// Informations de la societe (singleton)
// =============================================================================
export const companySettingsSchema = z.object({
    legalName: z.string().trim().min(2).max(150),
    tradeName: z.string().trim().min(2).max(150),
    siret: z
        .string()
        .trim()
        .regex(/^\d{14}$/, 'Le SIRET comporte 14 chiffres.')
        .optional()
        .nullable(),
    vatNumber: z.string().trim().max(20).optional().nullable(),
    email: emailSchema,
    phone: phoneSchema,
    street: z.string().trim().max(255),
    city: z.string().trim().max(100),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}$/),
    country: z.string().trim().max(60).default('France'),
    logoUrl: z.string().url().max(512).optional().nullable(),
    /** Texte libre affiche sur la page d'accueil : horaires, presentation. */
    openingHours: z.string().trim().max(2000).optional().nullable(),
    aboutText: z.string().trim().max(5000).optional().nullable(),
    legalNotice: z.string().trim().max(20000).optional().nullable(),
    termsAndConditions: z.string().trim().max(50000).optional().nullable(),
});
export const updateCompanySettingsSchema = companySettingsSchema.partial();
//# sourceMappingURL=catalog.js.map